import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  ToggleButton,
  ToggleButtonGroup,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import UndoIcon from '@mui/icons-material/Undo';
import BlockIcon from '@mui/icons-material/Block';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import AuthContext from '../../context/authContext';
import useAirKey from '../../hooks/useAirKey';
import useUser from '../../hooks/useUser';
import { toast } from 'react-toastify';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

function AirKeyManagement() {
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    sync, autoMatch, fetchPersons, fetchByUserId, fetchPersonProtocol,
    manualMatch, unmatch, markReviewed, deactivatePerson, isLoading,
    fetchAbgleich, fetchInactiveLinked, resyncForUser, reactivateForUser,
  } = useAirKey();
  const { fetchUsers } = useUser();

  // If navigated from user management with ?userId=xxx, show that person's detail
  const linkedUserId = searchParams.get('userId');
  const [linkedPerson, setLinkedPerson] = useState(null);
  const [linkedPersonLoading, setLinkedPersonLoading] = useState(false);

  useEffect(() => {
    if (!linkedUserId) {
      setLinkedPerson(null);
      return;
    }
    const load = async () => {
      setLinkedPersonLoading(true);
      try {
        const person = await fetchByUserId(linkedUserId);
        setLinkedPerson(person);
      } catch (err) {
        console.error('[AirKey] Failed to fetch linked person:', err);
        setLinkedPerson(null);
      } finally {
        setLinkedPersonLoading(false);
      }
    };
    load();
  }, [linkedUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  const [persons, setPersons] = useState([]);
  const [total, setTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [matching, setMatching] = useState(false);
  const [matchingPersonId, setMatchingPersonId] = useState(null);
  const [reviewingPersonId, setReviewingPersonId] = useState(null);
  const [deactivatingPersonId, setDeactivatingPersonId] = useState(null);
  const [deactivateDialogPerson, setDeactivateDialogPerson] = useState(null);
  const [protocolDialogPerson, setProtocolDialogPerson] = useState(null);
  const [view, setView] = useState('unmatched');
  const [sortField, setSortField] = useState('lastName');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const canSync = authCtx.checkRight('airkey:sync');
  const canMatch = authCtx.checkRight('airkey:match');
  const canUpdate = authCtx.checkRight('airkey:update');
  const canDeactivate = authCtx.checkRight('airkey:deactivate');
  const canActivate = authCtx.checkRight('airkey:activate') || authCtx.checkRight('airkey:activate:own');
  const [resyncingUserId, setResyncingUserId] = useState(null);
  const [unmatchingPersonId, setUnmatchingPersonId] = useState(null);

  // Debounce search
  const debounceTimer = useRef(null);
  useEffect(() => {
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 350);
    return () => clearTimeout(debounceTimer.current);
  }, [search]);

  // Fetch persons (server-side paginated)
  const [tableLoading, setTableLoading] = useState(false);

  const loadPersons = useCallback(async () => {
    setTableLoading(true);
    try {
      let res;
      if (view === 'abgleich') {
        res = await fetchAbgleich({
          page: page + 1,
          limit: rowsPerPage,
          search: debouncedSearch,
          onlyDiffs: true,
        });
      } else if (view === 'inactive') {
        res = await fetchInactiveLinked({
          page: page + 1,
          limit: rowsPerPage,
          search: debouncedSearch,
        });
      } else {
        res = await fetchPersons({
          page: page + 1,
          limit: rowsPerPage,
          search: debouncedSearch,
          sortField,
          sortDir,
          view,
        });
      }
      setPersons(res?.persons || []);
      setTotal(res?.total || 0);
    } catch (err) {
      console.error('[AirKey] Failed to fetch persons:', err);
      toast.error('Fehler beim Laden der AirKey-Personen');
    } finally {
      setTableLoading(false);
    }
  }, [fetchPersons, fetchAbgleich, fetchInactiveLinked, page, rowsPerPage, debouncedSearch, sortField, sortDir, view]);

  useEffect(() => {
    loadPersons();
  }, [loadPersons]);

  // Fetch users once for autocomplete
  useEffect(() => {
    const load = async () => {
      try {
        const usersRes = await fetchUsers();
        const userList = Array.isArray(usersRes) ? usersRes : usersRes?.users || [];
        setUsers(userList.filter(u => u.isActive));
      } catch (err) {
        console.error('[AirKey] Failed to fetch users:', err);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSync = useCallback(async () => {
    setSyncing(true);
    try {
      const result = await sync();
      toast.success(
        `Sync abgeschlossen: ${result.stats.airKeyTotal} Personen, ${result.stats.created} neu, ${result.stats.updated} aktualisiert`
      );
      await loadPersons();
    } catch (err) {
      toast.error('Fehler beim Synchronisieren mit AirKey');
    } finally {
      setSyncing(false);
    }
  }, [sync, loadPersons]);

  const handleAutoMatch = useCallback(async () => {
    setMatching(true);
    try {
      const result = await autoMatch();
      toast.success(
        `Auto-Match: ${result.matchedCount} zugeordnet, ${result.remainingUnmatched} verbleibend`
      );
      await loadPersons();
    } catch (err) {
      toast.error('Fehler beim Auto-Matching');
    } finally {
      setMatching(false);
    }
  }, [autoMatch, loadPersons]);

  const handleManualMatch = useCallback(
    async (airKeyPersonId, userId) => {
      setMatchingPersonId(airKeyPersonId);
      try {
        await manualMatch(airKeyPersonId, userId);
        toast.success('Zuordnung gespeichert');
        await loadPersons();
      } catch (err) {
        toast.error('Fehler bei manueller Zuordnung');
      } finally {
        setMatchingPersonId(null);
      }
    },
    [manualMatch, loadPersons]
  );

  const handleMarkReviewed = useCallback(
    async (airKeyPersonId, reviewed) => {
      setReviewingPersonId(airKeyPersonId);
      try {
        await markReviewed(airKeyPersonId, reviewed);
        toast.success(reviewed ? 'Als geprüft markiert' : 'Geprüft-Markierung entfernt');
        await loadPersons();
      } catch (err) {
        toast.error('Fehler beim Markieren');
      } finally {
        setReviewingPersonId(null);
      }
    },
    [markReviewed, loadPersons]
  );

  const handleDeactivate = useCallback(
    async person => {
      setDeactivateDialogPerson(null);
      setDeactivatingPersonId(person.airKeyPersonId);
      try {
        const result = await deactivatePerson(person.airKeyPersonId);
        const parts = [];
        if (result.phonesDeactivated > 0) parts.push(`${result.phonesDeactivated} Telefon(e) deaktiviert`);
        if (result.cardsEmptied > 0) parts.push(`${result.cardsEmptied} Karte(n) geleert`);
        if (result.cardsUnassigned > 0) parts.push(`${result.cardsUnassigned} Karte(n) getrennt`);
        if (result.commentUpdated) parts.push('Kommentar aktualisiert');
        toast.success(`Deaktivierung: ${parts.join(', ') || 'Keine Änderungen nötig'}`);
        if (result.errors?.length > 0) {
          toast.warn(`${result.errors.length} Fehler: ${result.errors[0]}`);
        }
        await loadPersons();
      } catch (err) {
        toast.error('Fehler beim Deaktivieren in AirKey');
      } finally {
        setDeactivatingPersonId(null);
      }
    },
    [deactivatePerson, loadPersons]
  );

  const handleResync = useCallback(
    async (userId) => {
      setResyncingUserId(userId);
      try {
        const result = await resyncForUser(userId);
        const parts = [];
        if (result.added > 0) parts.push(`${result.added} Berechtigung(en) hinzugefügt`);
        if (result.removed > 0) parts.push(`${result.removed} Berechtigung(en) entfernt`);
        toast.success(`Resync: ${parts.join(', ') || 'Bereits synchron'}`);
        if (result.errors?.length > 0) toast.warn(result.errors[0]);
        await loadPersons();
      } catch (err) {
        toast.error('Fehler beim AirKey-Resync');
      } finally {
        setResyncingUserId(null);
      }
    },
    [resyncForUser, loadPersons]
  );

  const handleUnmatch = useCallback(
    async (airKeyPersonId) => {
      setUnmatchingPersonId(airKeyPersonId);
      try {
        await unmatch(airKeyPersonId);
        toast.success('Zuordnung entfernt');
        await loadPersons();
      } catch (err) {
        toast.error('Fehler beim Entfernen der Zuordnung');
      } finally {
        setUnmatchingPersonId(null);
      }
    },
    [unmatch, loadPersons]
  );

  const userOptions = useMemo(() => {
    return users
      .filter(u => !u.airKeyPersonId)
      .map(u => ({
        id: u._id,
        label:
          `${u.firstName || ''} ${u.lastName || ''}`.trim() +
          (u.employeeId ? ` (${u.employeeId})` : ''),
        branches: (u.assignedBranches || []).map(b => b.name).join(', '),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [users]);

  const handleSort = field => {
    if (sortField === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(0);
  };

  if (!authCtx.checkRight('airkey:read')) {
    return <Navigate to='/welcome' replace />;
  }

  return (
    <Paper sx={{ p: 2, ...viewRootTextPrimarySx }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Typography variant='h6'>AirKey Abgleich</Typography>
        <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
          <ToggleButtonGroup
            value={view}
            exclusive
            size='small'
            onChange={(_, val) => {
              if (val) {
                setPersons([]);
                setTotal(0);
                setView(val);
                setPage(0);
              }
            }}
            sx={{ flexWrap: 'wrap' }}
          >
            <ToggleButton value='unmatched'>Offen</ToggleButton>
            <ToggleButton value='reviewed'>Geprüft</ToggleButton>
            <ToggleButton value='matched'>Zugeordnet</ToggleButton>
            <ToggleButton value='abgleich'>
              <CompareArrowsIcon sx={{ mr: 0.5, fontSize: 18 }} />
              Abgleich
            </ToggleButton>
            <ToggleButton value='inactive'>
              <PersonOffIcon sx={{ mr: 0.5, fontSize: 18 }} />
              Inaktiv
            </ToggleButton>
          </ToggleButtonGroup>
          {canUpdate && (
            <Button
              variant='outlined'
              startIcon={<SettingsIcon />}
              onClick={() => navigate('/airkey/mappings')}
            >
              Mappings
            </Button>
          )}
          {canSync && (
            <Button
              variant='outlined'
              startIcon={syncing ? <CircularProgress size={18} /> : <SyncIcon />}
              onClick={handleSync}
              disabled={syncing || matching}
            >
              Sync
            </Button>
          )}
          {canMatch && (
            <Button
              variant='outlined'
              startIcon={matching ? <CircularProgress size={18} /> : <AutoFixHighIcon />}
              onClick={handleAutoMatch}
              disabled={syncing || matching}
            >
              Auto-Match
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Linked person detail panel (from user management) */}
      {linkedUserId && (
        <LinkedPersonDetail
          person={linkedPerson}
          loading={linkedPersonLoading}
          canDeactivate={canDeactivate}
          canActivate={canActivate}
          canMatch={canMatch}
          onDeactivate={setDeactivateDialogPerson}
          onUnmatch={async (airKeyPersonId) => {
            try {
              await handleUnmatch(airKeyPersonId);
              setLinkedPerson(null);
            } catch (err) {
              // handled in handleUnmatch
            }
          }}
          onReactivate={async (person) => {
            try {
              const result = await reactivateForUser(linkedUserId);
              if (result.success) {
                toast.success(`Reaktivierung erfolgreich: ${result.details.phonesDeactivated} Telefon(e) deaktiviert, neues Medium + SMS erstellt`);
                // Refresh linked person
                const updated = await fetchByUserId(linkedUserId);
                setLinkedPerson(updated);
              } else {
                toast.warn(result.errors?.[0] || 'Teilweise reaktiviert');
              }
            } catch (err) {
              toast.error(err.message || 'Fehler beim Reaktivieren');
            }
          }}
          onShowProtocol={setProtocolDialogPerson}
          deactivatingPersonId={deactivatingPersonId}
          onClose={() => setSearchParams({})}
        />
      )}

      <TextField
        size='small'
        placeholder='Name, E-Mail, Telefon oder Standort suchen...'
        value={search}
        onChange={e => setSearch(e.target.value)}
        sx={{ mb: 2, width: { xs: '100%', sm: 400 } }}
      />

      <TableContainer sx={{ maxHeight: 'calc(100vh - 320px)' }}>
        <Table stickyHeader size='small'>
          <TableHead>
            <TableRow>
              <SortableHeader field='lastName' label='Name' sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <TableCell>Telefon (Medium)</TableCell>
              <TableCell>E-Mail</TableCell>
              <SortableHeader field='city' label='Stadt (AirKey)' sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <TableCell>Bereiche</TableCell>
              <TableCell>Medien</TableCell>
              <TableCell>Letzte Aktivität</TableCell>
              <TableCell>AirKey</TableCell>
              <TableCell sx={{ minWidth: 120 }}>Aktionen</TableCell>
              {view === 'matched' && <TableCell>Zugeordnet an</TableCell>}
              {view === 'abgleich' && <TableCell>Zugeordnet an</TableCell>}
              {view === 'abgleich' && <TableCell sx={{ minWidth: 200 }}>Abgleich</TableCell>}
              {view === 'inactive' && <TableCell>Zugeordnet an</TableCell>}
              {(view === 'unmatched' || view === 'reviewed') && canMatch && <TableCell sx={{ minWidth: 300 }}>Zuordnen</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {persons.map(person => (
              <AirKeyPersonRow
                key={person.airKeyPersonId}
                person={person}
                view={view}
                userOptions={userOptions}
                canMatch={canMatch}
                canDeactivate={canDeactivate}
                canActivate={canActivate}
                onMatch={handleManualMatch}
                onMarkReviewed={handleMarkReviewed}
                onDeactivate={setDeactivateDialogPerson}
                onShowProtocol={setProtocolDialogPerson}
                onResync={handleResync}
                onUnmatch={handleUnmatch}
                matchingPersonId={matchingPersonId}
                reviewingPersonId={reviewingPersonId}
                deactivatingPersonId={deactivatingPersonId}
                resyncingUserId={resyncingUserId}
                unmatchingPersonId={unmatchingPersonId}
              />
            ))}
            {persons.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align='center'>
                  {tableLoading ? (
                    <Box sx={{ ...viewRootTextPrimarySx, py: 4 }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : (
                    <Typography color='text.secondary' sx={{ py: 4 }}>
                      Keine Personen gefunden
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component='div'
        count={total}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={e => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[25, 50, 100]}
        labelRowsPerPage='Pro Seite:'
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} von ${count}`}
      />

      {/* Deactivation confirmation dialog */}
      <DeactivateDialog
        person={deactivateDialogPerson}
        onConfirm={handleDeactivate}
        onCancel={() => setDeactivateDialogPerson(null)}
      />

      {/* Protocol / log dialog */}
      <ProtocolDialog
        person={protocolDialogPerson}
        fetchProtocol={fetchPersonProtocol}
        onClose={() => setProtocolDialogPerson(null)}
      />
    </Paper>
  );
}

function LinkedPersonDetail({ person, loading, canDeactivate, canActivate, canMatch, onDeactivate, onReactivate, onUnmatch, onShowProtocol, deactivatingPersonId, onClose }) {
  const [reactivating, setReactivating] = useState(false);
  const [unmatching, setUnmatching] = useState(false);
  if (loading) {
    return (
      <Paper
        variant='outlined'
        sx={{ ...viewRootTextPrimarySx, p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}
      >
        <CircularProgress size={24} />
        <Typography>Lade AirKey-Daten...</Typography>
      </Paper>
    );
  }

  if (!person) {
    return (
      <Paper
        variant='outlined'
        sx={{
          ...viewRootTextPrimarySx,
          p: 2,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography color='text.secondary'>Keine AirKey-Zuordnung für diesen Benutzer gefunden.</Typography>
        <IconButton size='small' onClick={onClose}><CloseIcon fontSize='small' /></IconButton>
      </Paper>
    );
  }

  const phoneMedia = person.phoneMedia || [];
  const cardMedia = person.cardMedia || [];
  const areas = (person.areaAuthorizations || []).map(a => a.areaName).filter(Boolean);
  const hasActiveMedia = phoneMedia.some(m => m.activated) || cardMedia.some(m => m.activated);
  const isDeactivating = deactivatingPersonId === person.airKeyPersonId;

  return (
    <Paper variant='outlined' sx={{ ...viewRootTextPrimarySx, p: 2, mb: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent='space-between' alignItems='flex-start' spacing={2}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant='subtitle1' fontWeight={600}>
            AirKey-Daten: {person.firstName} {person.lastName}
            {person.secondaryIdentification && (
              <Typography component='span' variant='body2' color='text.secondary' sx={{ ml: 1 }}>
                (ID: {person.secondaryIdentification})
              </Typography>
            )}
          </Typography>
          <Stack direction='row' spacing={4} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
            <Box>
              <Typography variant='caption' color='text.secondary'>E-Mail</Typography>
              <Typography variant='body2'>{person.emailAddress || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant='caption' color='text.secondary'>Telefon (Kontakt)</Typography>
              <Typography variant='body2'>{person.phone || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant='caption' color='text.secondary'>Stadt</Typography>
              <Typography variant='body2'>{person.city || (person.derivedCities || []).join(', ') || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant='caption' color='text.secondary'>Match-Methode</Typography>
              <Typography variant='body2'>{person.matchMethod || '—'}</Typography>
            </Box>
          </Stack>
          <Stack direction='row' spacing={4} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
            <Box>
              <Typography variant='caption' color='text.secondary'>Handy-Medien</Typography>
              {phoneMedia.length > 0 ? (
                phoneMedia.map(m => (
                  <Typography key={m.mediaId} variant='body2'>
                    {m.phoneNumber || 'Unbekannt'} — {m.activated ? 'aktiv' : 'inaktiv'}
                    {m.appType ? ` (${m.appType})` : ''}
                  </Typography>
                ))
              ) : (
                <Typography variant='body2'>—</Typography>
              )}
            </Box>
            <Box>
              <Typography variant='caption' color='text.secondary'>Karten-Medien</Typography>
              {cardMedia.length > 0 ? (
                cardMedia.map(m => (
                  <Typography key={m.mediaId} variant='body2'>
                    {m.name || m.cardUid || `#${m.mediaId}`} — {m.activated ? 'aktiv' : 'inaktiv'}
                  </Typography>
                ))
              ) : (
                <Typography variant='body2'>—</Typography>
              )}
            </Box>
            <Box>
              <Typography variant='caption' color='text.secondary'>Bereiche</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {areas.length > 0
                  ? areas.map(a => <Chip key={a} label={a} size='small' variant='outlined' />)
                  : <Typography variant='body2'>—</Typography>}
              </Box>
            </Box>
          </Stack>
          {person.comment && (
            <Box sx={{ mt: 1 }}>
              <Typography variant='caption' color='text.secondary'>Kommentar</Typography>
              <Typography variant='body2' sx={{ whiteSpace: 'pre-line' }}>{person.comment}</Typography>
            </Box>
          )}
        </Box>
        <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap sx={{ flexShrink: 0 }}>
          {canActivate && person.matchedUserId && (
            <Tooltip title='Alle Handys deaktivieren, neues Handy anlegen und SMS senden'>
              <Button
                size='small'
                variant='outlined'
                color='warning'
                startIcon={reactivating ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={async () => {
                  setReactivating(true);
                  try {
                    await onReactivate(person);
                  } finally {
                    setReactivating(false);
                  }
                }}
                disabled={reactivating || isDeactivating}
              >
                Reaktivieren
              </Button>
            </Tooltip>
          )}
          {canDeactivate && hasActiveMedia && (
            <Tooltip title='In AirKey deaktivieren'>
              <Button
                size='small'
                variant='outlined'
                color='error'
                startIcon={isDeactivating ? <CircularProgress size={16} /> : <BlockIcon />}
                onClick={() => onDeactivate(person)}
                disabled={isDeactivating || reactivating}
              >
                Deaktivieren
              </Button>
            </Tooltip>
          )}
          {canMatch && person.matchedUserId && (
            <Tooltip title='Zuordnung entfernen (IDs lokal + secondaryIdentification in AirKey leeren)'>
              <Button
                size='small'
                variant='outlined'
                color='inherit'
                startIcon={unmatching ? <CircularProgress size={16} /> : <LinkOffIcon />}
                onClick={async () => {
                  setUnmatching(true);
                  try {
                    await onUnmatch(person.airKeyPersonId);
                  } finally {
                    setUnmatching(false);
                  }
                }}
                disabled={unmatching || reactivating || isDeactivating}
              >
                Entknüpfen
              </Button>
            </Tooltip>
          )}
          <Button
            size='small'
            variant='outlined'
            startIcon={<HistoryIcon />}
            onClick={() => onShowProtocol(person)}
          >
            Protokoll
          </Button>
          {person.airKeyLink && (
            <Tooltip title='In AirKey öffnen'>
              <IconButton
                size='small'
                component={Link}
                href={person.airKeyLink}
                target='_blank'
                rel='noopener noreferrer'
              >
                <OpenInNewIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size='small' onClick={onClose}><CloseIcon fontSize='small' /></IconButton>
        </Stack>
      </Stack>
    </Paper>
  );
}

function SortableHeader({ field, label, sortField, sortDir, onSort }) {
  return (
    <TableCell>
      <TableSortLabel
        active={sortField === field}
        direction={sortField === field ? sortDir : 'asc'}
        onClick={() => onSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
}

function DeactivateDialog({ person, onConfirm, onCancel }) {
  if (!person) return null;

  const phoneCount = (person.phoneMedia || []).length;
  const cardCount = (person.cardMedia || []).length;
  const areaCount = (person.areaAuthorizations || []).length;

  return (
    <Dialog open onClose={onCancel} maxWidth='sm' fullWidth>
      <DialogTitle>Person in AirKey deaktivieren</DialogTitle>
      <DialogContent>
        <DialogContentText>
          <strong>{person.firstName} {person.lastName}</strong> wird in AirKey deaktiviert.
          Folgende Aktionen werden durchgeführt:
        </DialogContentText>
        <Box component='ul' sx={{ mt: 1 }}>
          {areaCount > 0 && <li>{areaCount} Berechtigung(en) werden zum Löschen markiert</li>}
          {phoneCount > 0 && <li>{phoneCount} Handy-Medium/-Medien werden deaktiviert (Berechtigungen werden automatisch entfernt)</li>}
          {cardCount > 0 && <li>{cardCount} Karten-Medium/-Medien werden geleert und von der Person getrennt</li>}
          <li>Kommentar wird mit Besitzer-Info ergänzt</li>
        </Box>
        <DialogContentText sx={{ mt: 1, fontWeight: 600, color: 'error.main' }}>
          Dieser Vorgang kann nicht rückgängig gemacht werden.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Abbrechen</Button>
        <Button onClick={() => onConfirm(person)} color='error' variant='contained'>
          Deaktivieren
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const EVENT_TYPE_LABELS = {
  UNLOCKING_SUCCESSFUL: 'Entsperrt',
  UNLOCKING_SUCCESSFUL_VIA_HANDS_FREE: 'Entsperrt (Hands-Free)',
  UNLOCKING_FAILED: 'Entsperrung fehlgeschlagen',
  UNLOCKING_FAILED_BLACKLISTED: 'Gesperrt (Blacklist)',
  UNLOCKING_FAILED_NOT_WHITELISTED: 'Nicht berechtigt',
  CYLINDER_SYNCHRONIZATION_AS_PROXY_APP: 'Schließkomponente aktualisiert',
  KEY_RING_SYNCHRONIZATION_VIA_OTA: 'Medium aktualisiert',
};

const PROTOCOL_DAYS = 30;

function ProtocolDialog({ person, fetchProtocol, onClose }) {
  const [entries, setEntries] = useState([]);
  const [lastActivity, setLastActivity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [oldestFrom, setOldestFrom] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  // Initial load: last 30 days
  useEffect(() => {
    if (!person) return;
    setEntries([]);
    setLastActivity(null);
    setHasMore(true);
    setOldestFrom(null);

    const now = new Date();
    const from = new Date(now);
    from.setDate(from.getDate() - PROTOCOL_DAYS);

    const load = async () => {
      setLoading(true);
      try {
        const res = await fetchProtocol(person.airKeyPersonId, {
          from: from.toISOString(),
          to: now.toISOString(),
        });
        setEntries(res?.entries || []);
        setLastActivity(res?.lastActivity || null);
        setOldestFrom(from);
      } catch (err) {
        console.error('[AirKey] Failed to fetch protocol:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [person]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = useCallback(async () => {
    if (!person || !oldestFrom || loadingMore) return;
    setLoadingMore(true);
    try {
      const to = new Date(oldestFrom);
      const from = new Date(to);
      from.setDate(from.getDate() - PROTOCOL_DAYS);

      const res = await fetchProtocol(person.airKeyPersonId, {
        from: from.toISOString(),
        to: to.toISOString(),
      });
      const newEntries = res?.entries || [];
      if (newEntries.length === 0) {
        setHasMore(false);
      } else {
        setEntries(prev => [...prev, ...newEntries]);
        if (!lastActivity && res?.lastActivity) {
          setLastActivity(res.lastActivity);
        }
      }
      setOldestFrom(from);
    } catch (err) {
      console.error('[AirKey] Failed to load more protocol:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [person, oldestFrom, loadingMore, lastActivity, fetchProtocol]);

  if (!person) return null;

  const rangeLabel = oldestFrom
    ? `ab ${oldestFrom.toLocaleDateString('de-DE')}`
    : '';

  return (
    <Dialog open onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>
        Protokoll: {person.firstName} {person.lastName}
        {lastActivity && (
          <Typography variant='body2' color='text.secondary'>
            Letzte Aktivität: {new Date(lastActivity.timestamp).toLocaleString('de-DE')}
            {lastActivity.lockName ? ` — ${lastActivity.lockName}` : ''}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ ...viewRootTextPrimarySx, display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : entries.length === 0 ? (
          <Typography color='text.secondary' sx={{ py: 4, textAlign: 'center' }}>
            Keine Protokolleinträge gefunden ({rangeLabel})
          </Typography>
        ) : (
          <>
            <Typography variant='caption' color='text.secondary' sx={{ mb: 1, display: 'block' }}>
              {entries.length} Einträge ({rangeLabel})
            </Typography>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Zeitpunkt</TableCell>
                  <TableCell>Ereignis</TableCell>
                  <TableCell>Schloss</TableCell>
                  <TableCell>Medium</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry, idx) => {
                  const isSuccess = entry.eventType?.includes('SUCCESSFUL');
                  const isFail = entry.eventType?.includes('FAILED') || entry.eventType?.includes('BLACKLISTED');
                  return (
                    <TableRow key={idx} sx={isFail ? { bgcolor: 'error.50' } : undefined}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant='body2'>
                          {entry.timestamp ? new Date(entry.timestamp).toLocaleString('de-DE') : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={EVENT_TYPE_LABELS[entry.eventType] || entry.eventType || '—'}
                          size='small'
                          color={isSuccess ? 'success' : isFail ? 'error' : 'default'}
                          variant='outlined'
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2'>{entry.lockName || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2'>
                          {entry.mediumName || entry.mediumType || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant='caption'
                          sx={{ maxWidth: 300, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          {entry.details ? entry.details.replace(/<[^>]*>/g, '') : '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </>
        )}
        {!loading && hasMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              variant='text'
              onClick={handleLoadMore}
              disabled={loadingMore}
              startIcon={loadingMore ? <CircularProgress size={16} /> : <HistoryIcon />}
            >
              {loadingMore ? 'Lade...' : 'Weitere 30 Tage laden'}
            </Button>
          </Box>
        )}
        {!loading && !hasMore && entries.length > 0 && (
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
            Keine weiteren Einträge
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
}

function AirKeyPersonRow({
  person,
  view,
  userOptions,
  canMatch,
  canDeactivate,
  canActivate,
  onMatch,
  onMarkReviewed,
  onDeactivate,
  onShowProtocol,
  onResync,
  onUnmatch,
  matchingPersonId,
  reviewingPersonId,
  deactivatingPersonId,
  resyncingUserId,
  unmatchingPersonId,
}) {
  const [selectedUser, setSelectedUser] = useState(null);
  const isMatching = matchingPersonId === person.airKeyPersonId;
  const isReviewing = reviewingPersonId === person.airKeyPersonId;
  const isDeactivating = deactivatingPersonId === person.airKeyPersonId;
  const isUnmatching = unmatchingPersonId === person.airKeyPersonId;
  const matchedUserId = person.matchedUserId?._id || person.matchedUserId;
  const isResyncing = resyncingUserId && matchedUserId && String(resyncingUserId) === String(matchedUserId);

  const phoneNumbers = (person.phoneMedia || [])
    .map(m => m.phoneNumber)
    .filter(Boolean)
    .join(', ');

  const areas = (person.areaAuthorizations || []).map(a => a.areaName).filter(Boolean);

  const phoneCount = (person.phoneMedia || []).filter(m => m.activated).length;
  const phoneTotal = (person.phoneMedia || []).length;
  const cardCount = (person.cardMedia || []).filter(m => m.activated).length;
  const cardTotal = (person.cardMedia || []).length;
  const hasActiveMedia = phoneCount > 0 || cardCount > 0;

  return (
    <TableRow hover>
      <TableCell>
        <Typography variant='body2' fontWeight={500}>
          {person.firstName} {person.lastName}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant='body2' sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {phoneNumbers || '—'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant='body2' noWrap sx={{ maxWidth: 200 }}>
          {person.emailAddress || '—'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant='body2'>
          {person.city || (person.derivedCities || []).join(', ') || '—'}
        </Typography>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {areas.length > 0
            ? areas.map(a => <Chip key={a} label={a} size='small' variant='outlined' />)
            : '—'}
        </Box>
      </TableCell>
      <TableCell>
        <Stack spacing={0.25}>
          {phoneTotal > 0 && (
            <Typography variant='caption'>
              {phoneCount}/{phoneTotal} Handy{phoneTotal !== 1 ? 's' : ''} aktiv
            </Typography>
          )}
          {cardTotal > 0 && (
            <Typography variant='caption'>
              {cardCount}/{cardTotal} Karte{cardTotal !== 1 ? 'n' : ''} aktiv
            </Typography>
          )}
          {phoneTotal === 0 && cardTotal === 0 && (
            <Typography variant='caption' color='text.secondary'>—</Typography>
          )}
        </Stack>
      </TableCell>
      <TableCell>
        {person.lastActivity?.timestamp ? (
          <Tooltip title={person.lastActivity.lockName || ''}>
            <Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>
              {new Date(person.lastActivity.timestamp).toLocaleDateString('de-DE')}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant='caption' color='text.secondary'>—</Typography>
        )}
      </TableCell>
      <TableCell>
        <Tooltip title='In AirKey öffnen'>
          <IconButton
            size='small'
            component={Link}
            href={person.airKeyLink}
            target='_blank'
            rel='noopener noreferrer'
          >
            <OpenInNewIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Stack direction='row' spacing={0.5}>
          {canMatch && (
            <Tooltip title={person.reviewed ? 'Geprüft-Markierung entfernen' : 'Als geprüft markieren'}>
              <span>
                <IconButton
                  size='small'
                  color={person.reviewed ? 'success' : 'default'}
                  onClick={() => onMarkReviewed(person.airKeyPersonId, !person.reviewed)}
                  disabled={isReviewing}
                >
                  {isReviewing ? (
                    <CircularProgress size={18} />
                  ) : person.reviewed ? (
                    <UndoIcon fontSize='small' />
                  ) : (
                    <CheckCircleOutlineIcon fontSize='small' />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {canDeactivate && hasActiveMedia && (
            <Tooltip title='In AirKey deaktivieren'>
              <span>
                <IconButton
                  size='small'
                  color='error'
                  onClick={() => onDeactivate(person)}
                  disabled={isDeactivating}
                >
                  {isDeactivating ? <CircularProgress size={18} /> : <BlockIcon fontSize='small' />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title='Protokoll anzeigen'>
            <IconButton size='small' onClick={() => onShowProtocol(person)}>
              <HistoryIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          {(view === 'matched' || view === 'abgleich' || view === 'inactive') && canMatch && matchedUserId && (
            <Tooltip title='Zuordnung entfernen'>
              <span>
                <IconButton
                  size='small'
                  color='inherit'
                  onClick={() => onUnmatch(person.airKeyPersonId)}
                  disabled={isUnmatching}
                >
                  {isUnmatching ? <CircularProgress size={18} /> : <LinkOffIcon fontSize='small' />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {view === 'abgleich' && matchedUserId && canActivate && (
            <Tooltip title='Berechtigungen synchronisieren'>
              <span>
                <IconButton
                  size='small'
                  color='primary'
                  onClick={() => onResync(typeof matchedUserId === 'object' ? matchedUserId._id : matchedUserId)}
                  disabled={isResyncing}
                >
                  {isResyncing ? <CircularProgress size={18} /> : <SyncProblemIcon fontSize='small' />}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      </TableCell>
      {/* Matched user column for matched/abgleich/inactive views */}
      {(view === 'matched' || view === 'abgleich' || view === 'inactive') && (
        <TableCell>
          {person.matchedUserId && typeof person.matchedUserId === 'object' ? (
            <Box>
              <Typography variant='body2'>
                {person.matchedUserId.firstName} {person.matchedUserId.lastName}
                {person.matchedUserId.employeeId ? ` (${person.matchedUserId.employeeId})` : ''}
              </Typography>
              {view === 'inactive' && person.matchedUserId.deactivatedAt && (
                <Typography variant='caption' color='error'>
                  Deaktiviert: {new Date(person.matchedUserId.deactivatedAt).toLocaleDateString('de-DE')}
                </Typography>
              )}
              {view === 'inactive' && person.matchedUserId.isActive === false && !person.matchedUserId.deactivatedAt && (
                <Typography variant='caption' color='error'>Inaktiv</Typography>
              )}
            </Box>
          ) : '—'}
        </TableCell>
      )}
      {/* Abgleich diff column */}
      {view === 'abgleich' && (
        <TableCell>
          {person.diff ? (
            person.diff.inSync ? (
              <Chip label='Synchron' size='small' color='success' variant='outlined' />
            ) : (
              <Stack spacing={0.5}>
                {person.diff.missing.map(a => (
                  <Chip key={`m-${a.areaId}`} label={`+ ${a.areaName}`} size='small' color='warning' variant='outlined' />
                ))}
                {person.diff.extra.map(a => (
                  <Chip key={`e-${a.areaId}`} label={`- ${a.areaName}`} size='small' color='error' variant='outlined' />
                ))}
              </Stack>
            )
          ) : '—'}
        </TableCell>
      )}
      {/* Autocomplete for unmatched/reviewed views */}
      {(view === 'unmatched' || view === 'reviewed') && canMatch && (
        <TableCell>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Autocomplete
              size='small'
              sx={{ width: 250 }}
              options={userOptions}
              value={selectedUser}
              onChange={(_, value) => setSelectedUser(value)}
              getOptionLabel={opt => opt.label || ''}
              renderOption={(props, opt) => (
                <li {...props} key={opt.id}>
                  <Box>
                    <Typography variant='body2'>{opt.label}</Typography>
                    {opt.branches && (
                      <Typography variant='caption' color='text.secondary'>
                        {opt.branches}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={params => <TextField {...params} placeholder='User suchen...' />}
              isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            />
            <Button
              variant='contained'
              size='small'
              disabled={!selectedUser || isMatching}
              onClick={() => {
                if (selectedUser) {
                  onMatch(person.airKeyPersonId, selectedUser.id);
                  setSelectedUser(null);
                }
              }}
            >
              {isMatching ? <CircularProgress size={18} /> : 'Zuordnen'}
            </Button>
          </Stack>
        </TableCell>
      )}
    </TableRow>
  );
}

export default AirKeyManagement;
